import React, {useState} from 'react';

import cs from 'classnames';
import styled from 'styled-components';

import {useCurrentUserState, useUpdateCurrentUser} from 'app/current-user/api';
import {StyledPopoverBottomDIV} from 'app/general/bootstrap/utils';
import {AttachOverlay, PopoverForm} from 'app/general/overlays';
import StateButton from 'app/general/widgets/StateButton';
import {useUserReactedMap} from 'app/reactions/hooks';
import {
  availableReactions,
  modifiers,
  modifyReaction,
  skinNameToneMap,
  skinToneableReactions,
} from 'app/reactions/utils';
import {gt} from 'app/utils/i18n';

import {useAddReaction, useRemoveReaction} from '../api';

import {
  CommentReactionTypeWithTone,
  CommentSerializedType,
  ReactionSkinToneType,
} from 'sweet-types/comment/comment';

import type {CommentReactionsType} from 'sweet-types';

interface Props {
  reactions: CommentReactionsType;
  comment: CommentSerializedType;
  placement?: 'left' | 'bottom';
}

export const AddReactionButton = ({
  reactions,
  comment,
  placement = 'bottom',
}: Props): JSX.Element => {
  const userSkinName = useCurrentUserState().data?.emoji_skin_tone;

  const [addReaction] = useAddReaction();
  const [removeReaction] = useRemoveReaction();

  const [headerText, setHeaderText] = useState<string | null>(null);
  const [showSkinToneOptions, setShowSkinToneOptions] =
    useState<boolean>(false);
  const userReactionsMap = useUserReactedMap(reactions);

  const [updateCurrentUser] = useUpdateCurrentUser();
  const defaultSkinTone = userSkinName
    ? skinNameToneMap[userSkinName]
    : undefined;

  const handleSetDefaultSkinTone = (modifier?: ReactionSkinToneType) => {
    setShowSkinToneOptions(false);
    updateCurrentUser({
      emoji_skin_tone: modifier?.name ? modifier?.name : '',
    });
  };

  const normalThumbsUpEmoji = '👍';

  return (
    <AttachOverlay
      trigger="click"
      overlay={({overlayProps}) => (
        <PopoverForm overlayProps={{...overlayProps, placement}}>
          {({closeOnSuccess}) => (
            <>
              <StyledReactsPopoverDiv className="p-2 pt-0">
                <div className="border-bottom pb-1 small">
                  {headerText != null ? headerText : gt('Pick your reaction')}
                </div>
                <div className="pt-1">
                  <div className="d-flex flex-wrap align-items-center justify-content-center reacts-list">
                    {availableReactions.map(({icon, title, slug}) => {
                      const name =
                        userSkinName && skinToneableReactions.includes(icon)
                          ? (`${slug}::${userSkinName}` as CommentReactionTypeWithTone)
                          : slug;
                      const userHasReacted = userReactionsMap[name] === true;
                      const toggleReaction = userHasReacted
                        ? removeReaction
                        : addReaction;
                      return (
                        <StateButton
                          key={slug}
                          onClick={() =>
                            closeOnSuccess(
                              toggleReaction({
                                content_type: 'commentwithmeta',
                                object_id: comment.id,
                                name,
                              })
                            )
                          }
                          onMouseEnter={() =>
                            setHeaderText(
                              `${userHasReacted ? 'Remove' : ''} ${title}`
                            )
                          }
                          onMouseLeave={() => setHeaderText(null)}
                          className={cs(
                            'btn btn-link py-0 px-1 d-flex align-items-center justify-content-center',
                            {
                              active: userHasReacted,
                            }
                          )}
                        >
                          {modifyReaction(icon, defaultSkinTone?.tone)}
                        </StateButton>
                      );
                    })}
                  </div>
                </div>
              </StyledReactsPopoverDiv>
              <StyledPopoverBottomDIV>
                <div>
                  {showSkinToneOptions && defaultSkinTone && (
                    <button
                      onClick={() => handleSetDefaultSkinTone(undefined)}
                      className="p-0 border-0"
                    >
                      {normalThumbsUpEmoji}
                    </button>
                  )}
                  {showSkinToneOptions &&
                    modifiers
                      .filter(
                        (modifier) => modifier.tone != defaultSkinTone?.tone
                      )
                      .map((modifier, optionIndex) => (
                        <button
                          onClick={() => handleSetDefaultSkinTone(modifier)}
                          className="p-0 border-0"
                          key={optionIndex}
                        >
                          {modifyReaction(normalThumbsUpEmoji, modifier.tone)}
                        </button>
                      ))}
                  <button
                    onClick={() => setShowSkinToneOptions(!showSkinToneOptions)}
                    className="p-1 border-0 bg-light shadow"
                  >
                    {modifyReaction(normalThumbsUpEmoji, defaultSkinTone?.tone)}
                  </button>
                  <p className="small p-0 m-0">Default skin tone</p>
                </div>
              </StyledPopoverBottomDIV>
            </>
          )}
        </PopoverForm>
      )}
    >
      {(handlers) => (
        <button className="btn btn-link py-0 px-1" {...handlers}>
          <i className="far fa-fw fa-smile" />
        </button>
      )}
    </AttachOverlay>
  );
};
const StyledReactsPopoverDiv = styled.div`
  .reacts-list {
    width: 105px;

    button {
      width: 35px;
      height: 35px;
      transition: all 200ms;
      font-size: 1.2rem;

      &:active,
      &:hover,
      &:focus {
        text-decoration: none;
      }

      &:hover {
        font-size: 1.8em;
      }
    }
  }
`;
